import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Injector, OnDestroy, OnInit, signal } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { combineLatest, filter, map, skipWhile, Subject, switchMap, take, takeUntil, tap } from 'rxjs';

import { Cmg1003ConstantsService } from '../../../cmg-1003/services/cmg-1003-constants.service';
import { SessionQuery } from '../../../core/state';
import { Cmg1003LoanQuery, Cmg1003LoanStore, Cmg1003LookupsQuery, Cmg1003StoreQueryService } from '../../../loan-state';
import { Cmg1003FileDataQuery } from '../../../loan-state/file-data/file-data.query';
import { Cmg1003FileDataStore } from '../../../loan-state/file-data/file-data.store';
import { Models } from '../../../pipeline/models/global.models';
import { RouteApiService } from '../../../pipeline/stores/api/route-api.service';
import { calculateMinDate, formatDatePCalendar, removeEmptyNames, setupDateLogicSignals } from '../../utils';
import { applyPartialEnablingOrDisablingForSecondOrThirdLien } from '../../../core/utils';
import { SyncType } from '../../../models';
import { PostProcessingUrlaUiService } from '../../../loan-state/post.processing.urla.ui.service';
import { IdValue } from '../../models';
import { AddlProdAssistantModel } from '../../../cmg-1003/models';
import { DatePicker } from 'primeng/datepicker';
import { CmgFormControlDomDirective } from '../../directives/cmg-form-control-dom.directive';
import { CmgInputErrorDirective } from '../../directives/cmg-input-error.directive';
import { CmgPrimeNgDatePickerMaskDirective } from '../../directives/cmg-datepicker-mask.directive';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { QaSlugPipe } from '../../pipes/qa-slug.pipe';

@Component({
  selector: 'cmg-clear2-ui-loan-party-details',
  templateUrl: './loan-party-details.component.html',
  styleUrls: ['./loan-party-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    DatePicker,
    CmgFormControlDomDirective,
    CmgInputErrorDirective,
    CmgPrimeNgDatePickerMaskDirective,
    InputText,
    Select,
    QaSlugPipe,
  ],
})
export class LoanPartyDetailsComponent implements OnInit, OnDestroy {
  private readonly _fb = inject(UntypedFormBuilder);
  private readonly _sessionQuery = inject(SessionQuery);
  private readonly _fileDataQuery = inject(Cmg1003FileDataQuery);
  private readonly _fileDataStore = inject(Cmg1003FileDataStore);
  private readonly _loanQuery = inject(Cmg1003LoanQuery);
  private readonly _loanStore = inject(Cmg1003LoanStore);
  private readonly _api = inject(RouteApiService);
  private readonly _cmg1003ConstantsService = inject(Cmg1003ConstantsService);
  private readonly _storeQuery = inject(Cmg1003StoreQueryService);
  private readonly _postProcessingUrlaUiService = inject(PostProcessingUrlaUiService);
  private readonly _cmgLookupsQuery = inject(Cmg1003LookupsQuery);
  private readonly _cdRef = inject(ChangeDetectorRef);

  addlProdAssistant: AddlProdAssistantModel;
  addlProdAssistant2: AddlProdAssistantModel;
  addlProdAssistantsList: IdValue<string, string, string>[] = [];
  addlProdAssistants2List: IdValue<string, string, string>[] = [];

  loanPartyForm: UntypedFormGroup;

  loanProcessorList: Models.LoanOfficers[] = [];
  productionAssistantList: Models.LoanOfficers[];

  isFullyDisabled$ = this._storeQuery.statusRequiresDisabling$();

  jrloNamesList: IdValue<string, string, string>[] = [];

  minDate = signal<Date>(new Date());
  readonly maxDate = this._cmg1003ConstantsService.date200YearsAhead;
  readonly dateTimePickerCalendarZIndex = this._cmg1003ConstantsService.dateTimePickerCalendarZIndex;

  private _destroyed$ = new Subject<boolean>();
  private _isInitializing = true;

  private _injector = inject(Injector);

  ngOnInit() {
    this.loanPartyForm = this._fb.group({
      estClosingDate: [''],
      schedFundingDate: [''],
      firstPaymentDate: [''],
      processor: [''],
      productionAssistant: [''],
      originator: [{ value: '', disabled: true }],
      signingDate: [''],
      signingTime: [''],
      addlProdAssistant: [''],
      addlProdAssistant2: [''],
    });

    removeEmptyNames(this._cmgLookupsQuery.select((t) => t.jrloNames))
      .pipe(
        take(1),
        map((t) => this._copyNameToValueAndAddSelectOption(t)),
      )
      .subscribe((jrLoNames) => {
        this.jrloNamesList = jrLoNames;
      });

    this._cmgLookupsQuery
      .select((t) => t.additionalProdAssistants)
      .pipe(takeUntil(this._destroyed$))
      .subscribe((lists) => {
        this.addlProdAssistantsList = this._copyNameToValueAndAddSelectOption(lists?.addlProdAssistants?.filter((a) => !!a.name));
        this.addlProdAssistants2List = this._copyNameToValueAndAddSelectOption(lists?.addlProdAssistants2?.filter((a) => !!a.name));
      });

    this.isFullyDisabled$.pipe(takeUntil(this._destroyed$)).subscribe((isFullyDisabled) => {
      if (isFullyDisabled) {
        this.loanPartyForm.disable({
          onlySelf: true,
          emitEvent: false,
        });

        // This block must run after the form is disabled above.
        this._postProcessingUrlaUiService
          .enableFormControlBasedOnLoanStatus$('loanProcessorUserName')
          .pipe(take(1))
          .subscribe((isEnabled) => {
            if (isEnabled) {
              this.loanPartyForm.controls.processor.enable({ onlySelf: true, emitEvent: false });
            }
          });
      }
    });

    this._sessionQuery
      .select((state) => state.all1003Loaded)
      .pipe(
        filter((loaded) => loaded),
        takeUntil(this._destroyed$),
        switchMap(() =>
          combineLatest([this._fileDataQuery.select(), this._loanQuery.select()]).pipe(
            take(1),
            skipWhile((fileData, loan) => !fileData && !loan),
          ),
        ),
      )
      .subscribe(([fileData, loan]) => {
        this._isInitializing = true;

        this.minDate.set(calculateMinDate(loan.schedClosingDate, loan.schedFundingDate, loan.firstPaymentDate));

        this._cdRef.detectChanges();

        this.loanPartyForm.patchValue({
          processor: fileData.loanProcessorUserName,
          productionAssistant: fileData.otherUserName,
          originator: fileData.loanOfficerUserName,
          estClosingDate: loan.schedClosingDate ? formatDatePCalendar(loan.schedClosingDate) : null,
          schedFundingDate: loan.schedFundingDate ? formatDatePCalendar(loan.schedFundingDate) : null,
          firstPaymentDate: loan.firstPaymentDate ? formatDatePCalendar(loan.firstPaymentDate) : null,
          signingDate: formatDatePCalendar(loan.signingAppointmentDate),
          signingTime: loan.signingAppointmentTime,
          addlProdAssistant: '',
          addlProdAssistant2: '',
        });

        if (loan.addlProdAssistant) {
          this.addlProdAssistant = loan.addlProdAssistant;
          this.loanPartyForm.get('addlProdAssistant').setValue(loan.addlProdAssistant.value);
        }

        if (loan.addlProdAssistant2) {
          this.addlProdAssistant2 = loan.addlProdAssistant2;
          this.loanPartyForm.get('addlProdAssistant2').setValue(loan.addlProdAssistant2.value);
        }

        this._api
          .getLoanLpPaList$(fileData.organizationId)
          .pipe(
            tap((parties) => {
              this.loanProcessorList = parties.loanProcessorList;
              this.productionAssistantList = parties.productionAssistantList;
              this.loanPartyForm.get('processor').setValue(fileData.loanProcessorUserName);
              this.loanPartyForm.get('productionAssistant').setValue(fileData.otherUserName);
            }),
          )
          .subscribe();
        this.loanPartyForm
          .get('processor')
          .valueChanges.pipe(takeUntil(this._destroyed$))
          .subscribe((value) => {
            this._fileDataStore.update({ loanProcessorUserName: value as string });
          });
        this.loanPartyForm
          .get('productionAssistant')
          .valueChanges.pipe(takeUntil(this._destroyed$))
          .subscribe((value) => {
            this._fileDataStore.update({ otherUserName: value as string });
          });
        this.loanPartyForm
          .get('signingDate')
          .valueChanges.pipe(takeUntil(this._destroyed$))
          .subscribe((value) => {
            this._loanStore.update({ signingAppointmentDate: value as Date });
          });
        this.loanPartyForm
          .get('signingTime')
          .valueChanges.pipe(takeUntil(this._destroyed$))
          .subscribe((value) => {
            this._loanStore.update({ signingAppointmentTime: value as string });
          });
        this.loanPartyForm
          .get('addlProdAssistant')
          .valueChanges.pipe(takeUntil(this._destroyed$))
          .subscribe((value) => {
            const addlProdAssist: AddlProdAssistantModel = {
              ...(this.addlProdAssistant?.id && { id: this.addlProdAssistant.id }),
              value,
            };
            this._loanStore.update({ addlProdAssistant: addlProdAssist });
          });
        this.loanPartyForm
          .get('addlProdAssistant2')
          .valueChanges.pipe(takeUntil(this._destroyed$))
          .subscribe((value) => {
            const addlProdAssist2: AddlProdAssistantModel = {
              ...(this.addlProdAssistant2?.id && { id: this.addlProdAssistant2.id }),
              value,
            };
            this._loanStore.update({ addlProdAssistant2: addlProdAssist2 });
          });

        setupDateLogicSignals(
          {
            closingControl: this.loanPartyForm.get('estClosingDate'),
            fundingControl: this.loanPartyForm.get('schedFundingDate'),
            paymentControl: this.loanPartyForm.get('firstPaymentDate'),
            loanStore: this._loanStore,
            getLoanProgramCode: () => this._loanQuery.getValue().loanProgramCode,
          },
          this._injector,
        );

        Promise.resolve().then(() => {
          this._isInitializing = false;
        });
      });

    applyPartialEnablingOrDisablingForSecondOrThirdLien(
      false,
      ['signingDate', 'signingTime'],
      this.loanPartyForm,
      this._sessionQuery,
      this._fileDataQuery,
    );

    // The applyPartialEnablingOrDisablingForSecondOrThirdLien method above won't disable inputs if Loan Status is Approved.
    this._fileDataQuery
      .select((f) => f.syncType)
      .pipe(takeUntil(this._destroyed$))
      .subscribe((syncType) => {
        if (syncType === SyncType.SecondLien || syncType === SyncType.ThirdLien) {
          this.loanPartyForm.controls.signingDate.disable();
          this.loanPartyForm.controls.signingTime.disable();
        }
      });

    this.loanPartyForm.valueChanges.pipe(takeUntil(this._destroyed$)).subscribe(() => {
      if (this._isInitializing) {
        return;
      }

      if (this.loanPartyForm.pristine) {
        this.loanPartyForm.markAsDirty();
      }
    });
  }

  ngOnDestroy() {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  /**
   * _copyNameToValueAndAddSelectOption is a helper function to copy the array(since it is read-only)
   * and make name and value the same for JrLos but keep null, select for value and name of select option
   * @param idValueArray
   * @returns
   */
  private _copyNameToValueAndAddSelectOption(idValueArray: IdValue<string, string, string>[]): IdValue<string, string, string>[] {
    const mappedArray = [new IdValue(null, null, 'Select')];
    if (idValueArray && idValueArray.length > 0) {
      idValueArray
        .sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0))
        .forEach((t) => mappedArray.push(new IdValue(t.id, t.name, t.name)));
    }
    return mappedArray;
  }
}
